import { useEffect, useRef, useState } from "react";
import api, { inr } from "../../lib/api";
import { Card } from "./common";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { IndianRupee, Filter, RotateCcw, FileDown, ChevronDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PERIODS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "daily", label: "Daily (pick a date)" },
  { key: "this_month", label: "This Month" },
  { key: "previous_month", label: "Previous Month" },
  { key: "select_month", label: "Select Month" },
  { key: "custom", label: "Custom Date Range" },
];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const now = new Date();
const YEARS = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 4 + i);
const iso = (d) => d.toISOString().slice(0, 10);

export default function RevenueAnalytics() {
  const [programs, setPrograms] = useState([]);
  const [period, setPeriod] = useState("this_month");
  const [dateVal, setDateVal] = useState(iso(now));
  const [monthVal, setMonthVal] = useState(now.getMonth() + 1);
  const [yearVal, setYearVal] = useState(now.getFullYear());
  const [fromVal, setFromVal] = useState(iso(now));
  const [toVal, setToVal] = useState(iso(now));
  const [selected, setSelected] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progOpen, setProgOpen] = useState(false);
  const progRef = useRef();

  useEffect(() => { api.get("/programs").then((r) => setPrograms(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const h = (e) => { if (progRef.current && !progRef.current.contains(e.target)) setProgOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const buildParams = (p = period) => {
    const params = { period: p };
    if (p === "daily") params.date = dateVal;
    if (p === "select_month") { params.month = monthVal; params.year = yearVal; }
    if (p === "custom") { params.date_from = fromVal; params.date_to = toVal; }
    params.programs = selected.length ? selected.join(",") : "all";
    return params;
  };

  const load = async (p = period) => {
    setLoading(true);
    try {
      const { data } = await api.get("/dashboard/revenue", { params: buildParams(p) });
      setData(data);
    } catch (e) { toast.error("Could not load revenue data"); }
    setLoading(false);
  };

  useEffect(() => { load("this_month"); /* eslint-disable-next-line */ }, []);

  const reset = () => {
    setPeriod("this_month"); setDateVal(iso(now)); setMonthVal(now.getMonth() + 1); setYearVal(now.getFullYear());
    setFromVal(iso(now)); setToVal(iso(now)); setSelected([]);
    setTimeout(() => load("this_month"), 0);
  };
  const quick = (p) => { setPeriod(p); setTimeout(() => load(p), 0); };
  const toggleProg = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const exportData = async (fmt) => {
    try {
      const { data: blob } = await api.get("/dashboard/revenue/export", { params: { ...buildParams(), fmt }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([blob]));
      const a = document.createElement("a"); a.href = url; a.download = `gdiot_pay_revenue.${fmt === "xlsx" ? "xlsx" : "csv"}`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
  };

  const selLabel = selected.length === 0 ? "All Programs"
    : selected.length === 1 ? (programs.find((p) => p.id === selected[0])?.name || "1 program")
    : `${selected.length} programs`;
  const maxSeries = Math.max(1, ...((data?.series || []).map((d) => d.revenue)));

  return (
    <Card className="p-5 sm:p-6" data-testid="revenue-analytics">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-teal-50 text-teal-600 flex items-center justify-center"><IndianRupee className="w-5 h-5" /></div>
        <h3 className="font-head font-bold text-slate-900 text-lg">Revenue Analytics</h3>
      </div>

      {/* Quick buttons */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[["today", "Today"], ["this_month", "This Month"], ["previous_month", "Last Month"], ["custom", "Custom"]].map(([k, l]) => (
          <button key={k} onClick={() => quick(k)} data-testid={`revenue-quick-${k}`}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${period === k ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
        ))}
      </div>

      {/* Filter controls */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Revenue Period</label>
          <select value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="revenue-period-select"
            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
            {PERIODS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
          </select>
        </div>

        {period === "daily" && (
          <div>
            <label className="block text-xs font-semibold text-slate-600 mb-1.5">Select Date</label>
            <input type="date" value={dateVal} onChange={(e) => setDateVal(e.target.value)} data-testid="revenue-date"
              className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        )}
        {period === "select_month" && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Month</label>
              <select value={monthVal} onChange={(e) => setMonthVal(Number(e.target.value))} data-testid="revenue-month"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Year</label>
              <select value={yearVal} onChange={(e) => setYearVal(Number(e.target.value))} data-testid="revenue-year"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500">
                {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </>
        )}
        {period === "custom" && (
          <>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">From</label>
              <input type="date" value={fromVal} onChange={(e) => setFromVal(e.target.value)} data-testid="revenue-from"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">To</label>
              <input type="date" value={toVal} onChange={(e) => setToVal(e.target.value)} data-testid="revenue-to"
                className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </>
        )}

        {/* Program multi-select */}
        <div className="relative" ref={progRef}>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5">Program</label>
          <button type="button" onClick={() => setProgOpen((o) => !o)} data-testid="revenue-program-toggle"
            className="w-full bg-slate-50 border border-slate-300 rounded-lg px-3 py-2.5 text-sm flex items-center justify-between focus:outline-none focus:ring-2 focus:ring-blue-500">
            <span className="truncate">{selLabel}</span><ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>
          {progOpen && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl max-h-64 overflow-y-auto p-1" data-testid="revenue-program-menu">
              <button onClick={() => setSelected([])} data-testid="revenue-program-all"
                className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-slate-50">
                <span className={`w-4 h-4 rounded border flex items-center justify-center ${selected.length === 0 ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>{selected.length === 0 && <Check className="w-3 h-3 text-white" />}</span>
                All Programs
              </button>
              {programs.map((p) => (
                <button key={p.id} onClick={() => toggleProg(p.id)} data-testid={`revenue-program-${p.id}`}
                  className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left hover:bg-slate-50">
                  <span className={`w-4 h-4 rounded border flex items-center justify-center ${selected.includes(p.id) ? "bg-blue-600 border-blue-600" : "border-slate-300"}`}>{selected.includes(p.id) && <Check className="w-3 h-3 text-white" />}</span>
                  <span className="truncate">{p.name}</span>
                </button>
              ))}
              {programs.length === 0 && <p className="text-xs text-slate-400 px-3 py-2">No programs yet</p>}
            </div>
          )}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => load()} disabled={loading} data-testid="revenue-apply"
          className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-xl flex items-center gap-2 disabled:opacity-60">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Filter className="w-4 h-4" />} Apply Filter
        </button>
        <button onClick={reset} data-testid="revenue-reset"
          className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2.5 px-4 rounded-xl flex items-center gap-2"><RotateCcw className="w-4 h-4" /> Reset</button>
        <div className="flex-1" />
        <button onClick={() => exportData("csv")} data-testid="revenue-export-csv"
          className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2.5 px-4 rounded-xl flex items-center gap-2"><FileDown className="w-4 h-4" /> CSV</button>
        <button onClick={() => exportData("xlsx")} data-testid="revenue-export-xlsx"
          className="bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium py-2.5 px-4 rounded-xl flex items-center gap-2"><FileDown className="w-4 h-4" /> Excel</button>
      </div>

      {data && (
        <div className="mt-5 space-y-5">
          {/* Filter summary + primary card */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className={`lg:col-span-1 rounded-2xl p-5 text-white ${data.no_data ? "bg-slate-400" : "bg-blue-600"}`} data-testid="revenue-total-card">
              <div className="text-sm opacity-80">{data.no_data ? "No Revenue Found" : "Total Revenue"}</div>
              <div className="font-head text-3xl sm:text-4xl font-extrabold mt-1" data-testid="revenue-total">{inr(data.total)}</div>
              <div className="mt-2 inline-flex items-center gap-1.5 bg-white/15 rounded-full px-3 py-1 text-sm font-semibold" data-testid="revenue-count">{data.count.toLocaleString("en-IN")} transaction{data.count === 1 ? "" : "s"}</div>
              <div className="text-xs opacity-90 mt-2">Period: <b>{data.period_label}</b></div>
              <div className="text-xs opacity-90">Program: <b>{data.program_label}</b></div>
              {data.no_data && <div className="text-xs opacity-90 mt-2">No completed/accepted payments were found for the selected period and program.</div>}
            </div>
            <div className="lg:col-span-2">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                {data.granularity === "day" ? "Revenue" : "Revenue Trend"} — {data.period_label}
              </div>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.series} margin={{ left: -18, right: 8, top: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                    <YAxis tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} width={48} />
                    <Tooltip formatter={(v, n, item) => [`${inr(v)} · ${item?.payload?.count ?? 0} txn${item?.payload?.count === 1 ? "" : "s"}`, "Revenue"]} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
                    <Bar dataKey="revenue" fill="#2563EB" radius={[4, 4, 0, 0]} maxBarSize={maxSeries ? 40 : 40} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Breakdowns */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="border border-slate-200 rounded-2xl p-4" data-testid="revenue-by-program">
              <div className="text-sm font-semibold text-slate-800 mb-3">Revenue by Program — {data.period_label}</div>
              {data.by_program.length === 0 ? <p className="text-sm text-slate-400 py-4 text-center">No revenue in this period.</p> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-slate-400 text-xs uppercase tracking-wider"><th className="text-left font-semibold pb-2">Program</th><th className="text-right font-semibold pb-2">Txns</th><th className="text-right font-semibold pb-2">Revenue</th></tr></thead>
                  <tbody>
                    {data.by_program.map((r) => (
                      <tr key={r.program_id || "none"} className="border-t border-slate-100">
                        <td className="py-2 text-slate-700">{r.program_name}</td>
                        <td className="py-2 text-right text-slate-600" data-testid={`revenue-program-count-${r.program_id || "none"}`}>{r.count}</td>
                        <td className="py-2 text-right font-semibold text-slate-900">{inr(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="border border-slate-200 rounded-2xl p-4" data-testid="revenue-monthly-trend">
              <div className="text-sm font-semibold text-slate-800 mb-3">Monthly Revenue (last 12 months)</div>
              <div className="max-h-56 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-slate-400 text-xs uppercase tracking-wider"><th className="text-left font-semibold pb-2">Month</th><th className="text-right font-semibold pb-2">Txns</th><th className="text-right font-semibold pb-2">Revenue</th></tr></thead>
                  <tbody>
                    {data.monthly_trend.map((r) => (
                      <tr key={r.label} className="border-t border-slate-100">
                        <td className="py-2 text-slate-700">{r.label}</td>
                        <td className="py-2 text-right text-slate-600">{r.count}</td>
                        <td className="py-2 text-right font-medium text-slate-800">{inr(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}
